import PendingTaskShortcuts from 'components/organisms/pending-task-shortcuts'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Card, Button, Input, Table, Alert, Label } from 'reactstrap'
import { TitlePage } from 'components/atoms'
import SalesDealsApi from 'api/admins/sales-deals-api'

const getBuyingSourcingItemText = (deal) => {
  const list =
    deal?.buying_sourcing_product_detail_list ||
    deal?.buying_sourcing_product_detail_text ||
    deal?.sales_dashboard_product_detail_list ||
    deal?.sku_list_with_qty ||
    deal?.sku_list ||
    []

  if (Array.isArray(list)) return list.filter(Boolean).join('\n')
  return (list || '').toString()
}

const BuyingPoSourcingQueuePage = () => {
  const history = useHistory()
  const location = useLocation()
  const pendingTask = new URLSearchParams(location.search).get('pending_task') || undefined
  const legacySettlementUrl = new URLSearchParams(window.location.search).get('tab') === 'settlement'
  const [q, setQ] = useState('')
  const [appliedQ, setAppliedQ] = useState('')
  const [scope, setScope] = useState(new URLSearchParams(location.search).get('scope') || 'need_action')

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(null)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const load = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await SalesDealsApi.buyingPoSourcingQueue({
        scope,
        pending_task: pendingTask,
        q: appliedQ,
        page,
        per_page: perPage,
        sort_by: 'deals.updated_at',
        sort_dir: 'desc'
      })

      setRows(res?.data?.data || [])
      setMeta(res?.data?.meta || null)
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed load queue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [page, scope, appliedQ, perPage, pendingTask]) // eslint-disable-line

  useEffect(() => {
    if (legacySettlementUrl) history.replace(`/app/buying/po-settlement${window.location.search.replace(/([?&])tab=settlement&?/, '$1').replace(/[?&]$/, '')}`)
  }, [history, legacySettlementUrl])

  useEffect(() => {
    setScope(new URLSearchParams(location.search).get('scope') || 'need_action')
    setPage(1)
    setQ('')
    setAppliedQ('')
  }, [location.search])

  if (legacySettlementUrl) return null

  return (
    <>
      <TitlePage mainTitle="Buying — PO Sourcing Queue" />
      <PendingTaskShortcuts tasks={ ['buying_sources'] } />

      <Card body>
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <Label className="mb-1">Search</Label>
            <Input
              value={q}
              placeholder="Search order number / SKU / product..."
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedQ(q.trim())
                  setPage(1)
                }
              }}
              style={{ minWidth: 300 }}
            />
          </div>

          <div>
            <Label className="mb-1">State</Label>
            <Input
              type="select"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value)
                setPage(1)
              }}
              style={{ minWidth: 220 }}
            >
              <option value="need_action">Buying Team / All</option>
              <option value="on_sources">On Sources</option>
              <option value="sourced">Sourced</option>
              <option value="cancelled">Cancelled</option>
            </Input>
          </div>

          <div>
            <Label className="mb-1">Per Page</Label>
            <Input
              type="select"
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value, 10))
                setPage(1)
              }}
              style={{ minWidth: 120 }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Input>
          </div>

          <Button
            color="primary"
            onClick={() => {
              setAppliedQ(q.trim())
              setPage(1)
            }}
            disabled={loading}
          >
            Search
          </Button>

          <Button color="secondary" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>

        {error && <Alert color="danger" className="mt-3">{error}</Alert>}
        {loading && <Alert color="secondary" className="mt-3">Loading...</Alert>}

        <Table striped responsive className="mt-3" style={{ minWidth: 1550 }}>
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Deal Status</th>
              <th>SA</th>
              <th>Customer</th>
              <th style={{ minWidth: 200 }}>SKU</th>
              <th style={{ minWidth: 240 }}>Product Name</th>
              <th>Buying Price</th>
              <th>DP</th>
              <th>Selling Price</th>
              <th>Sourced By</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).length === 0 && !loading && (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>
                  No data found
                </td>
              </tr>
            )}

            {(rows || []).map((d) => {
              const customer =
                d?.user_detail?.name ||
                d?.user?.detail?.name ||
                d?.user?.name ||
                d?.user?.email ||
                '-'

              const sourcingItems = Array.isArray(d?.sourcing_items) ? d.sourcing_items : []
              const skuText = sourcingItems.map((item) => item.sku).filter(Boolean).join(', ') || (Array.isArray(d?.sku_list) ? d.sku_list.join(', ') : d?.sku_list) || '-'
              const productText = sourcingItems.map((item) => item.product_name).filter(Boolean).join('\n') || getBuyingSourcingItemText(d) || '-'
              const buyingPrice = sourcingItems.reduce((sum, item) => sum + (Number(item.buying_price || 0) * Number(item.quantity || 1)), 0)
              const sellingPrice = sourcingItems.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 1)), 0)

              return (
                <tr key={d.id} role='link' tabIndex={0} style={{ cursor: 'pointer' }} onClick={() => history.push(`/app/buying/po-sourcing/${d.id}`)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); history.push(`/app/buying/po-sourcing/${d.id}`) } }}>
                  <td>{d.reference_number}</td>
                  <td><b>{d.state}</b></td>
                  <td>{d.sales_assistant || d.sales_dashboard_sold_by || '-'}</td>
                  <td>{d.customer_name || customer}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{skuText}</td>
                  <td style={{ whiteSpace: 'pre-line', minWidth: 240 }}>{productText}</td>
                  <td>{buyingPrice || '-'}</td>
                  <td>{d.down_payment || 0}</td>
                  <td>{sellingPrice || '-'}</td>
                  <td>{d.sourced_by || '-'}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>

        {meta && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            <div>
              Page {meta.current_page} / {meta.total_pages} (Total: {meta.total_count})
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                color="secondary"
                disabled={loading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>

              <Button
                color="secondary"
                disabled={loading || page >= meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}

export default BuyingPoSourcingQueuePage
